import { CircularProgress, MenuItem, TextField } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api';

const channelId = (channel) => channel?.id || channel?.uuid || '';

const SalesChannelSelect = ({
    label,
    value,
    onChange,
    defaultName = 'Presencial',
    allowEmpty = false,
    emptyLabel = 'Todos',
    size = 'medium',
}) => {
    const [channels, setChannels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const didApplyDefault = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        api.get('sales-channels')
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const rows = Array.isArray(response.data?.channels) ? response.data.channels : [];
                setChannels(rows);
            })
            .catch(() => {
                if (!cancelled) {
                    setChannels([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (allowEmpty || isLoading || didApplyDefault.current || channels.length === 0) {
            return;
        }
        const ids = channels.map(channelId).filter(Boolean);
        if (value && ids.includes(value)) {
            didApplyDefault.current = true;
            return;
        }
        if (value) {
            didApplyDefault.current = true;
            return;
        }
        const preferred = channels.find((channel) => channel.name === defaultName) || channels[0];
        const nextId = channelId(preferred);
        if (!nextId || typeof onChange !== 'function') {
            return;
        }
        didApplyDefault.current = true;
        onChange({ target: { value: nextId } });
    }, [allowEmpty, isLoading, channels, value, defaultName, onChange]);

    const options = useMemo(() => {
        const rows = channels
            .map((channel) => ({
                id: channelId(channel),
                name: channel.name || '',
            }))
            .filter((channel) => channel.id && channel.name);
        if (value && !rows.some((channel) => channel.id === value)) {
            return [{ id: value, name: value }, ...rows];
        }
        return rows;
    }, [channels, value]);

    const selectValue = allowEmpty
        ? (value || '')
        : (options.some((channel) => channel.id === value) ? value : (value || ''));

    const handleChange = (event) => {
        if (typeof onChange !== 'function') {
            return;
        }
        const nextId = event.target.value;
        const selected = options.find((channel) => channel.id === nextId) || null;
        onChange(event, selected);
    };

    return (
        <TextField
            label={isLoading ? `${label} (carregando...)` : label}
            fullWidth
            select
            size={size}
            value={selectValue}
            onChange={handleChange}
            disabled={isLoading && options.length === 0}
        >
            {isLoading && options.length === 0 ? (
                <MenuItem value="">
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Carregando...
                </MenuItem>
            ) : null}
            {allowEmpty && !isLoading ? (
                <MenuItem value="">
                    {emptyLabel}
                </MenuItem>
            ) : null}
            {options.length === 0 && !isLoading ? (
                <MenuItem value="" disabled>
                    Nenhum canal cadastrado
                </MenuItem>
            ) : (
                options.map((channel) => (
                    <MenuItem key={channel.id} value={channel.id}>
                        {channel.name}
                    </MenuItem>
                ))
            )}
        </TextField>
    );
};

export default SalesChannelSelect;
